import React, { useId, useState } from 'react'
import { AlertTriangle, CheckCircle2, Circle, Info, XCircle } from 'lucide-react'
import { cn } from '../../lib/cn'
import { Button } from './Button'
import { useEntityShellLayout } from '../entity-view/layoutContext'

export type NextStepTone = 'ok' | 'warn' | 'bad' | 'info' | 'neutral'
export type NextStepChipTone = 'ok' | 'warn' | 'bad' | 'info' | 'none'

export interface NextStepAction {
  key: string
  label: string
  icon?: React.ReactNode
  onClick: () => void
  variant?: 'primary' | 'secondary'
  disabled?: boolean
  /** Почему кнопка неактивна: показывается подсказкой и делает кнопку неактивной. */
  disabledReason?: string
  loading?: boolean
}

export interface NextStepCheck {
  key: string
  label: string
  value?: React.ReactNode
  tone: NextStepChipTone
  /** Клик ведёт к месту, где проверку можно разобрать (вкладка окна). */
  onClick?: () => void
  /** Для tone 'none' — причина, почему данных нет. */
  title?: string
}

export interface NextStepBarProps {
  tone: NextStepTone
  title: React.ReactNode
  hint?: React.ReactNode
  actions?: NextStepAction[]
  checks?: NextStepCheck[]
  /** Плашка под строкой: текст ошибки площадки, предупреждение. */
  extra?: React.ReactNode
  className?: string
}

const background: Record<NextStepTone, string> = {
  ok: 'bg-emerald-50/70 dark:bg-emerald-950/20',
  warn: 'bg-amber-50/70 dark:bg-amber-950/25',
  bad: 'bg-red-50/70 dark:bg-red-950/25',
  info: 'bg-indigo-50/70 dark:bg-indigo-950/25',
  neutral: 'bg-white dark:bg-zinc-900',
}

const iconStyle: Record<
  NextStepTone,
  { Icon: React.ComponentType<{ className?: string }>; className: string }
> = {
  ok: { Icon: CheckCircle2, className: 'text-emerald-600 dark:text-emerald-400' },
  warn: { Icon: AlertTriangle, className: 'text-amber-600 dark:text-amber-400' },
  bad: { Icon: XCircle, className: 'text-red-600 dark:text-red-400' },
  info: { Icon: Info, className: 'text-indigo-600 dark:text-indigo-400' },
  neutral: { Icon: Circle, className: 'text-gray-400 dark:text-zinc-500' },
}

const chipTone: Record<NextStepChipTone, string> = {
  ok: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300',
  warn: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300',
  bad: 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300',
  info: 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-900 dark:bg-indigo-950/30 dark:text-indigo-300',
  none: 'border-gray-200 bg-gray-50 text-gray-500 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-400',
}

const dotTone: Record<NextStepChipTone, string> = {
  ok: 'bg-emerald-500',
  warn: 'bg-amber-500',
  bad: 'bg-red-500',
  info: 'bg-indigo-500',
  none: 'border border-dashed border-gray-400 bg-transparent dark:border-zinc-500',
}

const NO_DATA = 'нет данных'

const chipTitle = (check: NextStepCheck): string | undefined => {
  if (check.tone !== 'none') return check.title
  if (!check.title) return NO_DATA
  return check.title.toLowerCase().startsWith(NO_DATA) ? check.title : `${NO_DATA}: ${check.title}`
}

/**
 * NextStepBar — полоса «Что дальше» окна сущности.
 *
 * Единственное место окна, которое что-то утверждает: вывод одной фразой,
 * главное действие и сворачиваемый список всех проверок. Тон несёт иконка и мягкий фон, а не
 * цвет текста. Чип «нет данных» серый с пунктирной точкой: отсутствие данных
 * не должно выглядеть ни успехом, ни ошибкой.
 */
export const NextStepBar: React.FC<NextStepBarProps> = ({
  tone,
  title,
  hint,
  actions,
  checks,
  extra,
  className,
}) => {
  const [checksOpen, setChecksOpen] = useState(false)
  const checksId = useId()
  const issueCount = checks?.filter((check) => check.tone === 'bad' || check.tone === 'warn').length ?? 0
  const unknownCount = checks?.filter((check) => check.tone === 'none').length ?? 0
  const effectiveTone = tone === 'ok' && unknownCount > 0 ? 'neutral' : tone
  const { Icon, className: iconClass } = iconStyle[effectiveTone]
  const checksSummary = `Все проверки: ${checks?.length ?? 0}. Проблемы: ${issueCount}. Нет данных: ${unknownCount}.`
  // Узкое окно: вывод занимает строку целиком, кнопки уходят под него.
  const { narrow } = useEntityShellLayout()
  return (
    <div
      className={cn(
        'shrink-0 space-y-2.5 border-b border-gray-200 py-3 dark:border-zinc-800',
        narrow ? 'px-3' : 'px-5',
        background[effectiveTone],
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2.5">
        <div className={cn('flex min-w-0 flex-1 items-start gap-2.5', narrow && 'basis-full')}>
          <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', iconClass)} aria-hidden="true" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{title}</div>
            {hint && <div className="mt-0.5 text-xs text-gray-600 dark:text-zinc-400">{hint}</div>}
          </div>
        </div>
        {actions && actions.length > 0 && (
          <div className={cn('flex flex-wrap items-center gap-2', narrow ? 'basis-full' : 'shrink-0')}>
            {actions.map((action) => {
              const blocked = Boolean(action.disabled || action.disabledReason)
              return (
                <span key={action.key} title={action.disabledReason} className="inline-flex">
                  <Button
                    size="sm"
                    variant={action.variant === 'secondary' ? 'secondary' : 'primary'}
                    icon={action.icon}
                    loading={action.loading}
                    disabled={blocked}
                    onClick={action.onClick}
                  >
                    {action.label}
                  </Button>
                </span>
              )
            })}
          </div>
        )}
      </div>

      {checks && checks.length > 0 && (
        <details onToggle={(event) => setChecksOpen(event.currentTarget.open)}>
          <summary
            aria-label={checksSummary}
            aria-expanded={checksOpen}
            aria-controls={checksId}
            className="cursor-pointer rounded text-xs text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-zinc-400"
          >
            <span className="font-medium">Все проверки ({checks.length})</span>
            {issueCount > 0 && <span className="ml-2">Проблемы: {issueCount}</span>}
            {unknownCount > 0 && <span className="ml-2">Нет данных: {unknownCount}</span>}
          </summary>
          <ul id={checksId} className="m-0 mt-2 flex list-none flex-wrap items-center gap-1.5 p-0" aria-label="Проверки">
            {checks.map((check) => {
              const body = (
                <>
                  <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dotTone[check.tone])} />
                  <span>{check.label}</span>
                  {check.value !== undefined && check.value !== null && check.value !== '' && (
                    <span className="font-semibold tabular-nums">{check.value}</span>
                  )}
                </>
              )
              const chipClass = cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs',
                chipTone[check.tone],
              )
              return (
                <li key={check.key}>
                  {check.onClick ? (
                    <button
                      type="button"
                      onClick={check.onClick}
                      title={chipTitle(check)}
                      className={cn(
                        chipClass,
                        'transition-colors hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                      )}
                    >
                      {body}
                    </button>
                  ) : (
                    <span title={chipTitle(check)} className={chipClass}>
                      {body}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        </details>
      )}

      {extra}
    </div>
  )
}

export default NextStepBar
